import * as React from 'react';
import { ReactNode } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import Popover from '@mui/material/Popover';
import { type SxProps, type Theme, useTheme } from '@mui/material/styles';

export interface IField {
    type: string;
    value: string;
}

interface IDropdownProps {
    label: string;
    isDropdownActive: boolean;
    styles?: React.CSSProperties;
    className?: string;
    children?: ReactNode;
    sx?: SxProps<Theme>;
}

export const Dropdown: React.FC<IDropdownProps> = ({ label, styles, className, isDropdownActive, children, sx }) => {
    const theme = useTheme();
    const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    let sxOverrides: readonly object[] = [];
    if (Array.isArray(sx)) {
        sxOverrides = sx;
    } else if (sx) {
        sxOverrides = [sx];
    }

    return (
        <FormControl
            variant="standard"
            className={className}
            sx={[
                { '& .MuiOutlinedInput-notchedOutline': { border: 'none' } },
                ...sxOverrides,
            ]}
            size='small'
        >
            <button
                type='button'
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    border: 'none',
                    fontSize: '14px',
                    background: 'transparent',
                    ...styles,
                }}
                className={open ? 'dropdown_open' : 'dropdown_closed'}
                onClick={handleClick}
                disabled={React.Children.toArray(children).length === 0}
            >
                <span style={{ fontSize: '0.9rem', color:  isDropdownActive ? theme.palette.primary.accent : '' }} className='dropdown-label'>{label}</span>
                <ExpandMoreIcon
                    fontSize={'medium'}
                    sx={{
                        width: '1.1em',
                        height: '1.1em',
                        transform: open ? 'rotate(180deg)' : 'none',
                        color: isDropdownActive ? 'primary.accent' : ''
                    }}
                    data-testid='ExpandMoreIcon'
                />
            </button>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    '& .MuiPopover-paper': {
                        minWidth: '260px',
                        padding: '8px',
                    }
                }}
                className='dropdown-popover'
            >
                {
                    children
                }
            </Popover>
        </FormControl>
    );
};
